import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/Components/ui/card';
import { Button } from '@/Components/ui/button';
import { Input } from '@/Components/ui/input';
import { Textarea } from '@/Components/ui/textarea';
import { Label } from '@/Components/ui/label';
import { Badge } from '@/Components/ui/badge';
import { Separator } from '@/Components/ui/separator';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/Components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/Components/ui/dialog';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { API_BASE_URL } from '@/config/api';
import {
  Plus, Pencil, Trash2, Loader2, Calculator, ChevronUp, ChevronDown,
  Mic, List as ListIcon, CheckCircle2, ArrowLeft, Archive, Upload, X,
} from 'lucide-react';

/**
 * Company Aptitude Builder — create named, reusable aptitude tests
 * (sections → questions) served verbatim when a recruiter schedules an
 * aptitude test with source "Company template" or "Template + AI fill".
 *
 * Backend: /api/aptitude/templates CRUD (Web-Interviewer, company-scoped).
 */

const STANDARD_SECTIONS = [
  { key: 'quantitative',        label: 'Quantitative Aptitude' },
  { key: 'logical',             label: 'Logical Reasoning' },
  { key: 'verbal',              label: 'Verbal Ability' },
  { key: 'data_interpretation', label: 'Data Interpretation' },
  { key: 'abstract',            label: 'Abstract Reasoning' },
];

const emptyQuestion = () => ({
  question_type: 'mcq',
  question: '',
  options: ['', '', '', ''],
  correct_answer: '',
  explanation: '',
});

const emptySection = () => ({
  section_key: '',
  label: '',
  questions: [],
});

const api = (path, opts = {}) =>
  fetch(`${API_BASE_URL}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    ...opts,
  });

export default function AptitudeBuilderSection() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // null = list view; otherwise the template draft being edited
  const [draft, setDraft] = useState(null);
  const [confirmArchive, setConfirmArchive] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importErrors, setImportErrors] = useState([]);
  const [importNotices, setImportNotices] = useState([]);
  const [pdfPending, setPdfPending] = useState(null); // File awaiting AI-consent confirmation
  const fileInputRef = React.useRef(null);

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api('/api/aptitude/templates');
      if (!res.ok) throw new Error(`Status ${res.status}`);
      const data = await res.json();
      setTemplates(data.templates || []);
    } catch (e) {
      console.error('[AptitudeBuilder] list failed', e);
      toast.error('Could not load aptitude templates.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  // ── list actions ───────────────────────────────────────────────────────────
  const startCreate = () => {
    setImportErrors([]);
    setImportNotices([]);
    setDraft({ id: null, name: '', sections: [{ ...emptySection(), section_key: 'quantitative', label: 'Quantitative Aptitude' }] });
  };

  // ── file import ────────────────────────────────────────────────────────────
  const handleImportFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file
    if (!file) return;
    // PDFs go through AI extraction — get explicit consent first.
    if (/\.pdf$/i.test(file.name)) {
      setPdfPending(file);
      return;
    }
    doImportUpload(file);
  };

  const doImportUpload = async (file) => {
    const isPdf = /\.pdf$/i.test(file.name);
    setImporting(true);
    if (isPdf) {
      toast.info('AI is reading your PDF — this can take up to a minute for large files.');
    }
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await fetch(`${API_BASE_URL}/api/aptitude/templates/import`, {
        method: 'POST',
        credentials: 'include',
        body: form,
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 402) {
        toast.error(data.error || 'Importing a question bank costs 1 credit — your balance is too low. Please top up.');
        return;
      }
      if (!res.ok) throw new Error(data.error || `Status ${res.status}`);
      if (!data.imported) {
        setImportErrors(data.errors || []);
        toast.error('No questions could be imported from that file — check the row errors.');
        if (!(data.errors || []).length) return;
      }
      const baseName = file.name.replace(/\.(csv|xlsx|xlsm|pdf)$/i, '');
      setImportErrors(data.errors || []);
      setImportNotices(data.notices || []);
      setDraft({
        id: null,
        name: baseName,
        sections: (data.sections || []).map((s) => ({
          section_key: s.section_key,
          label: s.label,
          questions: (s.questions || []).map((q) => ({
            question_type: q.question_type,
            question: q.question,
            options: q.question_type === 'mcq'
              ? [...(q.options || []), '', '', '', ''].slice(0, 4)
              : ['', '', '', ''],
            correct_answer: q.correct_answer || '',
            explanation: q.explanation || '',
          })),
        })),
      });
      const noticeCount = (data.notices || []).length;
      toast.success(
        `Imported ${data.imported} question${data.imported === 1 ? '' : 's'} from ${file.name}` +
        (noticeCount ? ` (${noticeCount} converted — review them)` : '') +
        ((data.errors || []).length ? ` — ${data.errors.length} item${data.errors.length === 1 ? '' : 's'} need attention` : '') +
        '. Review and save.',
      );
    } catch (err) {
      console.error('[AptitudeBuilder] import failed', err);
      toast.error(err.message || 'Import failed — check the file format.');
    } finally {
      setImporting(false);
    }
  };

  const startEdit = async (tmpl) => {
    setImportErrors([]);
    setImportNotices([]);
    try {
      const res = await api(`/api/aptitude/templates/${tmpl.id}`);
      if (!res.ok) throw new Error(`Status ${res.status}`);
      const data = await res.json();
      const t = data.template;
      setDraft({
        id: t.id,
        name: t.name,
        sections: (t.sections || []).map((s) => ({
          section_key: s.section_key,
          label: s.label,
          questions: (s.questions || []).map((q) => ({
            question_type: q.question_type,
            question: q.question,
            options: q.question_type === 'mcq'
              ? [...(q.options || []), '', '', '', ''].slice(0, 4)
              : ['', '', '', ''],
            correct_answer: q.correct_answer || '',
            explanation: q.explanation || '',
          })),
        })),
      });
    } catch (e) {
      console.error('[AptitudeBuilder] load failed', e);
      toast.error('Could not open that template.');
    }
  };

  const archiveTemplate = async (tmpl) => {
    try {
      const res = await api(`/api/aptitude/templates/${tmpl.id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(`Status ${res.status}`);
      toast.success(`Archived "${tmpl.name}"`);
      setConfirmArchive(null);
      loadTemplates();
    } catch (e) {
      console.error('[AptitudeBuilder] archive failed', e);
      toast.error('Could not archive the template.');
    }
  };

  // ── draft helpers ──────────────────────────────────────────────────────────
  const updateSection = (si, patch) =>
    setDraft((d) => ({
      ...d,
      sections: d.sections.map((s, i) => (i === si ? { ...s, ...patch } : s)),
    }));

  const moveSection = (si, dir) =>
    setDraft((d) => {
      const sections = [...d.sections];
      const j = si + dir;
      if (j < 0 || j >= sections.length) return d;
      [sections[si], sections[j]] = [sections[j], sections[si]];
      return { ...d, sections };
    });

  const removeSection = (si) =>
    setDraft((d) => ({ ...d, sections: d.sections.filter((_, i) => i !== si) }));

  const updateQuestion = (si, qi, patch) =>
    setDraft((d) => ({
      ...d,
      sections: d.sections.map((s, i) =>
        i === si
          ? { ...s, questions: s.questions.map((q, j) => (j === qi ? { ...q, ...patch } : q)) }
          : s),
    }));

  const removeQuestion = (si, qi) =>
    setDraft((d) => ({
      ...d,
      sections: d.sections.map((s, i) =>
        i === si ? { ...s, questions: s.questions.filter((_, j) => j !== qi) } : s),
    }));

  // ── validation + save ──────────────────────────────────────────────────────
  const validateDraft = () => {
    if (!draft.name.trim()) return 'Give the template a name.';
    if (!draft.sections.length) return 'Add at least one section.';
    for (const [si, s] of draft.sections.entries()) {
      if (!s.label.trim()) return `Section ${si + 1} needs a name.`;
      if (!s.questions.length) return `Section "${s.label}" has no questions yet.`;
      for (const [qi, q] of s.questions.entries()) {
        if (!q.question.trim()) return `Question ${qi + 1} in "${s.label}" is empty.`;
        if (q.question_type === 'mcq') {
          const opts = q.options.map((o) => o.trim());
          if (opts.some((o) => !o)) return `Question ${qi + 1} in "${s.label}" needs all 4 options filled.`;
          if (!opts.includes(q.correct_answer.trim())) return `Question ${qi + 1} in "${s.label}" needs a correct option selected.`;
        } else if (!q.correct_answer.trim()) {
          return `${q.question_type === 'written' ? 'Written' : 'Verbal'} question ${qi + 1} in "${s.label}" needs an expected answer.`;
        }
      }
    }
    return null;
  };

  const saveDraft = async () => {
    const error = validateDraft();
    if (error) { toast.error(error); return; }
    setSaving(true);
    try {
      const payload = {
        name: draft.name.trim(),
        sections: draft.sections.map((s) => ({
          section_key: s.section_key || undefined,
          label: s.label.trim(),
          questions: s.questions.map((q) => ({
            question_type: q.question_type,
            question: q.question.trim(),
            options: q.question_type === 'mcq' ? q.options.map((o) => o.trim()) : [],
            correct_answer: q.correct_answer.trim(),
            explanation: (q.explanation || '').trim(),
          })),
        })),
      };
      const res = await api(
        draft.id ? `/api/aptitude/templates/${draft.id}` : '/api/aptitude/templates',
        { method: draft.id ? 'PUT' : 'POST', body: JSON.stringify(payload) },
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `Status ${res.status}`);
      toast.success(draft.id ? 'Template updated' : 'Template created');
      setDraft(null);
      loadTemplates();
    } catch (e) {
      console.error('[AptitudeBuilder] save failed', e);
      toast.error(e.message || 'Could not save the template.');
    } finally {
      setSaving(false);
    }
  };

  // ── render: editor ─────────────────────────────────────────────────────────
  if (draft) {
    return (
      <div className="flex flex-col gap-4">
        {importErrors.length > 0 && (
          <Card className="border-amber-300 dark:border-amber-700 bg-amber-50/60 dark:bg-amber-950/20">
            <CardContent className="pt-4 pb-3">
              <div className="flex items-start justify-between gap-2">
                <div className="text-sm">
                  <p className="font-semibold text-amber-800 dark:text-amber-300 mb-1">
                    {importErrors.length} item{importErrors.length === 1 ? '' : 's'} need attention
                  </p>
                  <ul className="text-xs text-amber-700 dark:text-amber-400 space-y-0.5 max-h-32 overflow-y-auto">
                    {importErrors.map((er, i) => (
                      <li key={i}>{er.label !== undefined ? (er.label ? `${er.label}: ` : '') : `Row ${er.row}: `}{er.reason}</li>
                    ))}
                  </ul>
                </div>
                <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={() => setImportErrors([])}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
        {importNotices.length > 0 && (
          <Card className="border-sky-300 dark:border-sky-700 bg-sky-50/60 dark:bg-sky-950/20">
            <CardContent className="pt-4 pb-3">
              <div className="flex items-start justify-between gap-2">
                <div className="text-sm">
                  <p className="font-semibold text-sky-800 dark:text-sky-300 mb-1">
                    {importNotices.length} question{importNotices.length === 1 ? '' : 's'} converted during import — just review them
                  </p>
                  <ul className="text-xs text-sky-700 dark:text-sky-400 space-y-0.5 max-h-32 overflow-y-auto">
                    {importNotices.map((n, i) => (
                      <li key={i}>{n.label ? `${n.label}: ` : ''}{n.reason}</li>
                    ))}
                  </ul>
                </div>
                <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={() => setImportNotices([])}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <Button variant="ghost" size="sm" onClick={() => { setDraft(null); setImportErrors([]); setImportNotices([]); }} className="gap-1.5">
            <ArrowLeft className="w-4 h-4" /> Back to templates
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setDraft(null)} disabled={saving}>Cancel</Button>
            <Button onClick={saveDraft} disabled={saving} className="gap-1.5">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              {draft.id ? 'Save changes' : 'Create template'}
            </Button>
          </div>
        </div>

        <Card>
          <CardContent className="pt-5 space-y-2">
            <Label className="text-sm font-semibold">Template name</Label>
            <Input
              value={draft.name}
              onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
              placeholder='e.g. "Campus Hiring 2026 — Round 1"'
              className="max-w-md"
            />
          </CardContent>
        </Card>

        {draft.sections.map((section, si) => (
          <Card key={si}>
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2 flex-wrap">
                <Select
                  value={STANDARD_SECTIONS.some((s) => s.key === section.section_key) ? section.section_key : 'custom'}
                  onValueChange={(v) => {
                    if (v === 'custom') updateSection(si, { section_key: '', label: section.label || '' });
                    else {
                      const std = STANDARD_SECTIONS.find((s) => s.key === v);
                      updateSection(si, { section_key: std.key, label: std.label });
                    }
                  }}
                >
                  <SelectTrigger className="w-[230px] h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STANDARD_SECTIONS.map((s) => (
                      <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
                    ))}
                    <SelectItem value="custom">Custom section…</SelectItem>
                  </SelectContent>
                </Select>
                {!STANDARD_SECTIONS.some((s) => s.key === section.section_key) && (
                  <Input
                    value={section.label}
                    onChange={(e) => updateSection(si, { label: e.target.value })}
                    placeholder='Custom section name, e.g. "Banking Awareness"'
                    className="w-[260px] h-9"
                  />
                )}
                <Badge variant="secondary" className="text-xs">
                  {section.questions.length} question{section.questions.length === 1 ? '' : 's'}
                </Badge>
                <div className="ml-auto flex items-center gap-1">
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => moveSection(si, -1)} disabled={si === 0} title="Move up">
                    <ChevronUp className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => moveSection(si, 1)} disabled={si === draft.sections.length - 1} title="Move down">
                    <ChevronDown className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => removeSection(si)} title="Remove section">
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {section.questions.map((q, qi) => (
                <div key={qi} className="rounded-lg border border-border p-3 space-y-2.5">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-[10px]">Q{qi + 1}</Badge>
                    <Select
                      value={q.question_type}
                      onValueChange={(v) => updateQuestion(si, qi, {
                        question_type: v,
                        // verbal ↔ written share the expected-answer field — keep it;
                        // only clear when switching to/from MCQ (option ≠ expected answer)
                        correct_answer: (v !== 'mcq' && q.question_type !== 'mcq') ? q.correct_answer : '',
                      })}
                    >
                      <SelectTrigger className="w-[150px] h-8 text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="mcq"><span className="flex items-center gap-1.5"><ListIcon className="w-3.5 h-3.5" /> MCQ</span></SelectItem>
                        <SelectItem value="voice"><span className="flex items-center gap-1.5"><Mic className="w-3.5 h-3.5" /> Verbal answer</span></SelectItem>
                        <SelectItem value="written"><span className="flex items-center gap-1.5"><Pencil className="w-3.5 h-3.5" /> Written answer</span></SelectItem>
                      </SelectContent>
                    </Select>
                    <Button variant="ghost" size="icon" className="h-7 w-7 ml-auto" onClick={() => removeQuestion(si, qi)} title="Remove question">
                      <Trash2 className="w-3.5 h-3.5 text-destructive" />
                    </Button>
                  </div>
                  <Textarea
                    value={q.question}
                    onChange={(e) => updateQuestion(si, qi, { question: e.target.value })}
                    placeholder="Question text…"
                    className="min-h-[60px] text-sm"
                  />
                  {q.question_type === 'mcq' ? (
                    <div className="space-y-1.5">
                      {q.options.map((opt, oi) => (
                        <div key={oi} className="flex items-center gap-2">
                          <input
                            type="radio"
                            name={`correct-${si}-${qi}`}
                            checked={q.correct_answer === opt && opt !== ''}
                            onChange={() => updateQuestion(si, qi, { correct_answer: opt })}
                            className="cursor-pointer"
                            title="Mark as correct answer"
                          />
                          <Input
                            value={opt}
                            onChange={(e) => {
                              const options = [...q.options];
                              const wasCorrect = q.correct_answer === options[oi] && options[oi] !== '';
                              options[oi] = e.target.value;
                              updateQuestion(si, qi, {
                                options,
                                correct_answer: wasCorrect ? e.target.value : q.correct_answer,
                              });
                            }}
                            placeholder={`Option ${oi + 1}`}
                            className="h-8 text-sm"
                          />
                        </div>
                      ))}
                      <p className="text-xs text-muted-foreground">Select the radio next to the correct option.</p>
                    </div>
                  ) : (
                    <Textarea
                      value={q.correct_answer}
                      onChange={(e) => updateQuestion(si, qi, { correct_answer: e.target.value })}
                      placeholder="Expected answer / what a full-credit answer contains…"
                      className="min-h-[50px] text-sm"
                    />
                  )}
                  <Input
                    value={q.explanation}
                    onChange={(e) => updateQuestion(si, qi, { explanation: e.target.value })}
                    placeholder="Explanation (optional — shown in the recruiter report)"
                    className="h-8 text-sm"
                  />
                </div>
              ))}
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5"
                onClick={() => updateSection(si, { questions: [...section.questions, emptyQuestion()] })}
              >
                <Plus className="w-3.5 h-3.5" /> Add question
              </Button>
            </CardContent>
          </Card>
        ))}

        <Button
          variant="outline"
          className="gap-1.5 self-start"
          onClick={() => setDraft((d) => ({ ...d, sections: [...d.sections, emptySection()] }))}
        >
          <Plus className="w-4 h-4" /> Add section
        </Button>
      </div>
    );
  }

  // ── render: list ───────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <Calculator className="w-4 h-4 text-primary" /> Aptitude Templates
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                Build your own aptitude tests, section by section. Templates are reusable across
                every job — pick one when scheduling an aptitude test.
              </p>
            </div>
            <div className="flex gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx,.xlsm,.pdf"
                className="hidden"
                onChange={handleImportFile}
              />
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={importing}
                className="gap-1.5"
                title="Upload a CSV/XLSX question bank (columns: section, type, question, option_a–d, answer, explanation) or a PDF (AI-extracted). Importing costs 1 credit; building manually is free."
              >
                {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                Import from file
                <Badge variant="secondary" className="ml-1 text-[10px] px-1.5 py-0">1 credit</Badge>
              </Button>
              <Button onClick={startCreate} className="gap-1.5">
                <Plus className="w-4 h-4" /> New template
              </Button>
            </div>
            <p className="text-[11px] text-muted-foreground mt-1.5">
              Importing a question bank deducts <span className="font-medium">1 credit</span>. Building a template manually is free.
            </p>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center gap-2 text-muted-foreground py-6 justify-center">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading templates…
            </div>
          ) : templates.length === 0 ? (
            <div className="text-center py-8 text-sm text-muted-foreground">
              No templates yet. Create your first aptitude template to serve your own questions
              instead of (or alongside) automated ones.
            </div>
          ) : (
            <div className="flex flex-col">
              {templates.map((t, i) => (
                <React.Fragment key={t.id}>
                  {i > 0 && <Separator />}
                  <div className="flex items-center gap-3 py-3 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-foreground truncate">{t.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {t.section_count} section{t.section_count === 1 ? '' : 's'} · {t.question_count} question{t.question_count === 1 ? '' : 's'}
                        {t.updated_at ? ` · updated ${new Date(t.updated_at).toLocaleDateString()}` : ''}
                      </p>
                    </div>
                    <Button variant="outline" size="sm" className="gap-1.5" onClick={() => startEdit(t)}>
                      <Pencil className="w-3.5 h-3.5" /> Edit
                    </Button>
                    <Button variant="ghost" size="sm" className="gap-1.5 text-destructive" onClick={() => setConfirmArchive(t)}>
                      <Archive className="w-3.5 h-3.5" /> Archive
                    </Button>
                  </div>
                </React.Fragment>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* PDF AI-extraction consent */}
      <Dialog open={!!pdfPending} onOpenChange={(o) => !o && setPdfPending(null)}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-4 h-4 text-primary" /> Import questions from PDF
            </DialogTitle>
            <DialogDescription className="space-y-2.5 pt-1">
              <span className="block">
                We'll use AI to read <b>"{pdfPending?.name}"</b> and pull the questions
                out for you — no retyping needed.
              </span>
              <span className="block">A few things you should know:</span>
              <span className="block pl-1">
                ✓ Your file is used only for this import. We don't keep a copy, and it
                is never used to train AI.
                <br />
                ✓ Nothing is saved automatically — you review and approve every
                question first.
                <br />
                ✓ If the PDF doesn't clearly show an answer, that question is
                highlighted so you can set the correct answer yourself. The AI never
                guesses.
              </span>
              <span className="block text-xs">
                Don't want AI to read your file? Fill in our Excel/CSV template
                instead — those files are read directly by our own system.
              </span>
              <span className="block text-xs font-medium text-foreground">
                This import will deduct 1 credit.
              </span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setPdfPending(null)}>Cancel</Button>
            <Button
              onClick={() => { const f = pdfPending; setPdfPending(null); doImportUpload(f); }}
              className="gap-1.5"
            >
              <CheckCircle2 className="w-4 h-4" /> Continue with AI extraction
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Archive confirmation */}
      <Dialog open={!!confirmArchive} onOpenChange={(o) => !o && setConfirmArchive(null)}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle>Archive template?</DialogTitle>
            <DialogDescription>
              "{confirmArchive?.name}" will no longer appear when scheduling aptitude tests.
              Tests already scheduled with it are not affected.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setConfirmArchive(null)}>Cancel</Button>
            <Button variant="destructive" onClick={() => archiveTemplate(confirmArchive)} className="gap-1.5">
              <Archive className="w-4 h-4" /> Archive
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
